import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBranding } from "@/contexts/BrandContext";

/** Number of brand header links shown inline before overflowing into a menu. */
const INLINE_LIMIT = 5;

/**
 * Renders the active brand's configured `header_links` in the top header.
 *
 * For the platform-default brand `header_links` is empty (`is_default: true`),
 * so this renders nothing and the header stays pixel-identical to the
 * pre-white-label build. When a dealer brand supplies links, up to
 * {@link INLINE_LIMIT} appear inline (desktop only, `md:flex`); any beyond that
 * collapse into a "More" dropdown so the header never overflows.
 *
 * Links are plain `{label, url}` strings (URLs are validated server-side); they
 * render as anchors with `target="_blank" rel="noopener noreferrer"` — never via
 * `dangerouslySetInnerHTML`.
 */
export function HeaderBrandLinks() {
  const { header_links } = useBranding();
  const { t } = useTranslation();

  if (header_links.length === 0) {
    return null;
  }

  const inline = header_links.slice(0, INLINE_LIMIT);
  const overflow = header_links.slice(INLINE_LIMIT);

  return (
    <div className="flex items-center gap-4">
      {/* Subtle divider separating brand links from the app-chrome controls.
          Desktop only — on mobile the inline nav is hidden, so a lone divider
          would just float next to the More menu. Kept tight to the links
          (small right margin) so it reads as their leading edge, not a gap. */}
      <div
        aria-hidden="true"
        className="hidden h-5 w-px bg-gray-200 md:block"
      />

      {/* Inline links — desktop only; below md the whole set is reachable from
          the dispatcher sidebar's mobile links block. */}
      <nav className="hidden items-center gap-5 md:flex">
        {inline.map((link, index) => (
          <a
            key={index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-sm text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/50 focus-visible:ring-offset-1"
          >
            {link.label}
          </a>
        ))}
      </nav>

      {/* Overflow menu — only rendered when there are more links than the inline
          limit. Anchors are wrapped via `asChild` so they keep native
          target/rel semantics (no window.open). */}
      {overflow.length > 0 && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label={t("nav.moreLinks")}
              className="flex items-center gap-1 rounded-sm text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/50 focus-visible:ring-offset-1"
            >
              {t("nav.more")}
              <ChevronDown size={14} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-[180px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
            >
              {overflow.map((link, index) => (
                <DropdownMenu.Item key={index} asChild>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-700 outline-none hover:bg-gray-50 focus:bg-gray-50"
                  >
                    {link.label}
                  </a>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </div>
  );
}
